import { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { recaptchaHeaders } from "../lib/recaptcha";

const AuthContext = createContext(null);

const TOKEN_KEY = "admin_token";
const ADMIN_KEY = "admin_info";
const REFRESH_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes

function decodeTokenPayload(token) {
  try {
    const payload = token.split(".")[1];
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
}

function isTokenExpired(token) {
  const payload = decodeTokenPayload(token);
  if (!payload?.exp) return true;
  return Date.now() / 1000 >= payload.exp;
}

// Synchronously set or remove the Authorization header on axios defaults.
// This runs before any component renders, so there is no window where a
// request can fire without the token.
function applyTokenToAxios(token) {
  if (token) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common["Authorization"];
  }
}

// Apply immediately at module load — before React renders anything
const initialToken = localStorage.getItem(TOKEN_KEY);
if (initialToken && !isTokenExpired(initialToken)) {
  applyTokenToAxios(initialToken);
}

// Response interceptor registered once at module level — never re-added on
// re-renders, never races with component mount effects.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || "";
      // Only force-logout for admin API calls, not the login endpoint itself
      if (url.includes("/admin/") && !url.includes("/auth/login")) {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(ADMIN_KEY);
        applyTokenToAxios(null);
        window.location.replace("/admin/login");
      }
    }
    return Promise.reject(error);
  }
);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => {
    const t = localStorage.getItem(TOKEN_KEY);
    return t && !isTokenExpired(t) ? t : null;
  });
  const [admin, setAdmin] = useState(() => {
    try {
      const stored = localStorage.getItem(ADMIN_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const refreshTimerRef = useRef(null);
  const navigate = useNavigate();

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ADMIN_KEY);
    applyTokenToAxios(null);
    setToken(null);
    setAdmin(null);
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    navigate("/admin/login");
  }, [navigate]);

  const saveSession = useCallback((newToken, adminData) => {
    localStorage.setItem(TOKEN_KEY, newToken);
    localStorage.setItem(ADMIN_KEY, JSON.stringify(adminData));
    applyTokenToAxios(newToken); // immediate — no effect delay
    setToken(newToken);
    setAdmin(adminData);
  }, []);

  const refreshToken = useCallback(async () => {
    const currentToken = localStorage.getItem(TOKEN_KEY);
    if (!currentToken || isTokenExpired(currentToken)) {
      logout();
      return;
    }
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/refresh`,
        {}
        // No manual header needed — axios.defaults already has it
      );
      const newToken = res.data.access_token;
      localStorage.setItem(TOKEN_KEY, newToken);
      applyTokenToAxios(newToken);
      setToken(newToken);
    } catch {
      logout();
    }
  }, [logout]);

  // 10-minute silent refresh while the tab is open
  useEffect(() => {
    if (!token) return;
    refreshTimerRef.current = setInterval(refreshToken, REFRESH_INTERVAL_MS);
    return () => clearInterval(refreshTimerRef.current);
  }, [token, refreshToken]);

  const login = useCallback(async (username, password) => {
    const rcHeaders = await recaptchaHeaders("login");
    const res = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/api/auth/login`,
      { username, password },
      { headers: rcHeaders }
    );
    const { access_token, admin: adminData } = res.data;
    saveSession(access_token, adminData);
    return adminData;
  }, [saveSession]);

  const isAuthenticated = Boolean(token && !isTokenExpired(token));

  return (
    <AuthContext.Provider value={{ token, admin, isAuthenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
